import { useMemo, useState } from "react";
import { Clock, LayoutGrid, List, Search } from "lucide-react";

type ProjectStatus = "Draft" | "In Progress" | "Completed";

interface Project {
  name: string;
  spend: string;
  client: string;
  status: ProjectStatus;
  step: string;
  progress: number;
  owner: string;
  updated: string;
}

const PROJECTS: Project[] = [
  { name: "Global Indirect Spend Analysis 2024", spend: "$847M", client: "Acme Corporation", status: "In Progress", step: "Supplier Mapping", progress: 67, owner: "Sarah Chen", updated: "2h ago" },
  { name: "APAC Supplier Rationalization", spend: "$213M", client: "Global Dynamics Ltd", status: "In Progress", step: "Upload Data", progress: 33, owner: "James Park", updated: "1d ago" },
  { name: "IT Services Benchmark Study", spend: "$64M", client: "TechCore Inc", status: "Completed", step: "Review", progress: 100, owner: "Amara Diallo", updated: "3d ago" },
  { name: "Logistics Contract Optimization", spend: "$128M", client: "Meridian Freight Co", status: "Draft", step: "Project Details", progress: 10, owner: "Lena Fischer", updated: "5d ago" },
  { name: "Marketing Agency Consolidation", spend: "$42M", client: "BrightPath Media", status: "Completed", step: "Review", progress: 100, owner: "Daniel Ortiz", updated: "1w ago" },
];

const FILTERS = ["All", "Draft", "In Progress", "Completed"] as const;
type Filter = (typeof FILTERS)[number];

const STATUS_MOD: Record<ProjectStatus, string> = {
  Draft: "draft",
  "In Progress": "progress",
  Completed: "done",
};

interface Props {
  onStart: () => void;
}

export function ProjectsListView({ onStart }: Props) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("All");
  const [layout, setLayout] = useState<"list" | "grid">("list");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PROJECTS.filter(
      (p) =>
        (filter === "All" || p.status === filter) &&
        (!q || p.name.toLowerCase().includes(q) || p.client.toLowerCase().includes(q)),
    );
  }, [query, filter]);

  const inProgress = PROJECTS.filter((p) => p.status === "In Progress").length;

  return (
    <div className="pl-page">
      <div className="pl-header">
        <div>
          <p className="pl-header__eyebrow">Spend Insights</p>
          <h2 className="pl-header__title">All Projects</h2>
          <p className="pl-header__meta">{PROJECTS.length} projects · {inProgress} in progress</p>
        </div>
        <div className="pl-header__actions">
          <button type="button" className="pl-btn pl-btn--outline">Import</button>
          <button type="button" className="pl-btn pl-btn--primary" onClick={onStart}>Create New Project</button>
        </div>
      </div>

      <div className="pl-toolbar">
        <label className="pl-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="text"
            className="pl-search__input"
            placeholder="Search projects..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        <div className="pl-toolbar__right">
          <div className="pl-segment" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                className={`pl-segment__btn${filter === f ? " pl-segment__btn--active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="pl-segment pl-segment--icons">
            <button
              type="button"
              aria-label="List view"
              className={`pl-segment__btn${layout === "list" ? " pl-segment__btn--soft" : ""}`}
              onClick={() => setLayout("list")}
            >
              <List size={16} />
            </button>
            <button
              type="button"
              aria-label="Grid view"
              className={`pl-segment__btn${layout === "grid" ? " pl-segment__btn--soft" : ""}`}
              onClick={() => setLayout("grid")}
            >
              <LayoutGrid size={16} />
            </button>
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="pl-empty">No projects found. Try adjusting your search or filter.</div>
      ) : layout === "list" ? (
        <div className="pl-card">
          <table className="pl-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Client</th>
                <th>Status</th>
                <th>Current Step</th>
                <th>Progress</th>
                <th>Owner</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.name}>
                  <td>
                    <p className="pl-project__name">{p.name}</p>
                    <p className="pl-project__spend">{p.spend} spend analyzed</p>
                  </td>
                  <td className="pl-table__client">{p.client}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td className="pl-table__step">{p.step}</td>
                  <td><ProgressBar status={p.status} value={p.progress} /></td>
                  <td><Owner name={p.owner} /></td>
                  <td><Updated value={p.updated} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="pl-grid">
          {visible.map((p) => (
            <div key={p.name} className="pl-card pl-tile">
              <div className="pl-tile__top">
                <StatusBadge status={p.status} />
                <Updated value={p.updated} />
              </div>
              <p className="pl-project__name">{p.name}</p>
              <p className="pl-project__spend">{p.client} · {p.spend} spend analyzed</p>
              <p className="pl-table__step">Current step: {p.step}</p>
              <ProgressBar status={p.status} value={p.progress} />
              <Owner name={p.owner} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ProjectStatus }) {
  return (
    <span className={`pl-status pl-status--${STATUS_MOD[status]}`}>
      <span className="pl-status__dot" />
      {status}
    </span>
  );
}

function ProgressBar({ status, value }: { status: ProjectStatus; value: number }) {
  return (
    <div className="pl-progress">
      <div className="pl-progress__track">
        <div className={`pl-progress__fill pl-progress__fill--${STATUS_MOD[status]}`} style={{ width: `${value}%` }} />
      </div>
      <span className="pl-progress__value">{value}%</span>
    </div>
  );
}

function Owner({ name }: { name: string }) {
  return (
    <div className="pl-owner">
      <span className="pl-owner__avatar">{name.charAt(0)}</span>
      <span className="pl-owner__name">{name}</span>
    </div>
  );
}

function Updated({ value }: { value: string }) {
  return (
    <span className="pl-updated">
      <Clock size={12} aria-hidden="true" />
      {value}
    </span>
  );
}
